// ============================================================
// SPRINKLES BY RUCHI — App Logic v11 (Complete Rewrite)
// App-quality UX: instant sheets, smooth transitions, 60fps
// ============================================================
'use strict';

const SP = {
  WA: '9097193931',
  scrollY: 0,
  locked: false,
  carouselIdx: 0,
  carouselTimer: null,
  currentProduct: null,
  selectedSize: null,
  currentCatName: '',
};

// ════════════════════════════════
// SCROLL LOCK — Preserves scroll position
// ════════════════════════════════
function spLock() {
  if (SP.locked) return;
  SP.locked = true;
  SP.scrollY = window.scrollY;
  document.documentElement.style.setProperty('--sp-scroll-top', `-${SP.scrollY}px`);
  document.body.classList.add('sp-locked');
}
function spUnlock() {
  if (!SP.locked) return;
  SP.locked = false;
  document.body.classList.remove('sp-locked');
  document.documentElement.style.removeProperty('--sp-scroll-top');
  window.scrollTo({ top: SP.scrollY, behavior: 'instant' });
}

// ════════════════════════════════
// HEADER — Adaptive transparency
// ════════════════════════════════
function initHeader() {
  const header = document.querySelector('.sp-hdr'); // .sp-hdr is the actual class in header.liquid
  const bar = document.querySelector('.sp-announce'); // announcement bar
  if (!header) return;
  let ticking = false;
  function update() {
    const y = window.scrollY;
    const barH = bar ? bar.offsetHeight : 0;
    const mob = window.innerWidth <= 768;
    header.style.top = mob ? '0' : Math.max(0, barH - y) + 'px';
    if (y > 50) { header.classList.add('scrolled'); header.classList.remove('at-hero'); }
    else { header.classList.remove('scrolled'); header.classList.add('at-hero'); }
    ticking = false;
  }
  update();
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  setTimeout(update, 300);
}

// ════════════════════════════════
// MOBILE MENU — Slide in/out
// ════════════════════════════════
function initMobileMenu() {
  // Class names match header.liquid: .sp-burger, .sp-m-nav, .sp-m-x, .sp-m-ov
  const btn = document.querySelector('.sp-burger');
  const nav = document.querySelector('.sp-m-nav');
  const close = document.querySelector('.sp-m-x');
  const ovl = document.querySelector('.sp-m-ov');
  if (!btn || !nav) return;
  function open() { nav.classList.add('open'); ovl?.classList.add('open'); spLock(); }
  function shut() { nav.classList.remove('open'); ovl?.classList.remove('open'); spUnlock(); }
  btn.addEventListener('click', open);
  close?.addEventListener('click', shut);
  ovl?.addEventListener('click', shut);
  nav.querySelectorAll('a').forEach(a => a.addEventListener('click', shut));
}

// ════════════════════════════════
// CAROUSEL — Auto-advance, swipe, dots
// ════════════════════════════════
function initCarousel() {
  const wrap = document.querySelector('.sp-carousel-wrap');
  const track = document.querySelector('.sp-carousel-track');
  const dotsW = document.querySelector('.sp-car-dots');
  if (!track || track.children.length === 0) return;
  if (SP.carouselTimer) { clearInterval(SP.carouselTimer); SP.carouselTimer = null; }

  function vis() {
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  }
  function go(idx, anim) {
    const cards = track.querySelectorAll('.sp-prod-card');
    if (!cards.length) return;
    const v = vis(), max = Math.max(0, cards.length - v);
    SP.carouselIdx = ((idx % (max + 1)) + (max + 1)) % (max + 1);
    const gap = parseFloat(getComputedStyle(track).gap) || 20;
    const w = cards[0].getBoundingClientRect().width;
    track.style.transition = anim === false ? 'none' : 'transform .5s cubic-bezier(.22,1,.36,1)';
    track.style.transform = `translateX(-${SP.carouselIdx * (w + gap)}px)`;
    dotsW?.querySelectorAll('.sp-car-dot').forEach((d, i) => d.classList.toggle('active', i === SP.carouselIdx));
  }
  function buildDots() {
    if (!dotsW) return;
    const count = Math.max(1, track.querySelectorAll('.sp-prod-card').length - vis() + 1);
    dotsW.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const d = document.createElement('button');
      d.className = 'sp-car-dot' + (i === 0 ? ' active' : '');
      d.setAttribute('aria-label', `Slide ${i + 1}`);
      d.addEventListener('click', () => { stopA(); go(i); startA(); });
      dotsW.appendChild(d);
    }
  }
  function next() { const max = Math.max(0, track.querySelectorAll('.sp-prod-card').length - vis()); go(SP.carouselIdx >= max ? 0 : SP.carouselIdx + 1); }
  function prev() { const max = Math.max(0, track.querySelectorAll('.sp-prod-card').length - vis()); go(SP.carouselIdx <= 0 ? max : SP.carouselIdx - 1); }
  function startA() { stopA(); SP.carouselTimer = setInterval(next, 4000); }
  function stopA() { if (SP.carouselTimer) { clearInterval(SP.carouselTimer); SP.carouselTimer = null; } }

  document.querySelector('.sp-car-btn.prev')?.addEventListener('click', () => { stopA(); prev(); startA(); });
  document.querySelector('.sp-car-btn.next')?.addEventListener('click', () => { stopA(); next(); startA(); });

  let tx = 0;
  track.addEventListener('touchstart', e => { tx = e.touches[0].clientX; stopA(); }, { passive: true });
  track.addEventListener('touchend', e => { const d = tx - e.changedTouches[0].clientX; if (Math.abs(d) > 40) d > 0 ? next() : prev(); startA(); }, { passive: true });
  if (wrap) { wrap.addEventListener('mouseenter', stopA); wrap.addEventListener('mouseleave', startA); }

  buildDots(); go(0, false); startA();
  let rt;
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { SP.carouselIdx = 0; buildDots(); go(0, false); }, 250); });
}

// ════════════════════════════════
// SCROLL FADE-IN — IntersectionObserver
// ════════════════════════════════
function initFadeIn() {
  if (!('IntersectionObserver' in window)) { document.querySelectorAll('.sp-fade').forEach(el => el.classList.add('visible')); return; }
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); } });
  }, { threshold: 0.08 });
  document.querySelectorAll('.sp-fade').forEach(el => io.observe(el));
}

// ════════════════════════════════
// LAZY-LOAD IMAGES — Native with fallback
// ════════════════════════════════
function initLazyImages() {
  // Use IntersectionObserver for true lazy loading — only load images near viewport
  const io = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          obs.unobserve(img);
          _spRevealImg(img);
        });
      }, { rootMargin: '200px 0px' })   // start loading 200px before entering viewport
    : null;

  document.querySelectorAll('img[loading="lazy"]').forEach((img, i) => {
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('sp-img-loaded');
      return;
    }
    img.classList.add('sp-img-loading');
    if (io) {
      // Store real src in data-src and blank it so browser doesn't auto-fetch
      if (!img.dataset.src && img.src && !img.src.endsWith('/')) {
        img.dataset.src = img.src;
        // Only blank non-first-4 images; first 4 load immediately (likely above fold)
        if (i >= 4) {
          img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E';
        }
      }
      io.observe(img);
    } else {
      _spRevealImg(img);
    }
  });
}

function _spRevealImg(img) {
  const src = img.dataset.src || img.src;
  if (!src || src.startsWith('data:')) return;
  const tempImg = new Image();
  tempImg.onload = () => {
    img.src = src;
    img.classList.remove('sp-img-loading');
    img.classList.add('sp-img-loaded');
  };
  tempImg.onerror = () => {
    img.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&q=70';
    img.classList.remove('sp-img-loading');
    img.classList.add('sp-img-loaded');
  };
  tempImg.src = src;
}

// ════════════════════════════════
// DRAG TO CLOSE — Swipe down gesture
// ════════════════════════════════
function addDragClose(handleEl, sheetEl, closeFn) {
  if (!handleEl || !sheetEl) return;
  let startY = 0, delta = 0, active = false, vel = 0, lastY = 0, lastT = 0;
  const onS = y => { startY = y; lastY = y; delta = 0; active = true; vel = 0; lastT = Date.now(); sheetEl.style.transition = 'none'; };
  const onM = y => {
    if (!active) return;
    const dy = y - startY;
    if (dy < 0) return;
    delta = dy;
    const now = Date.now(), dt = now - lastT;
    if (dt > 0) vel = (y - lastY) / dt;
    lastY = y; lastT = now;
    sheetEl.style.transform = `translateY(${dy}px)`;
    const bg = sheetEl.closest('.sp-modal-bg, .sp-order-bg');
    if (bg) bg.style.background = `rgba(0,0,0,${Math.max(0.1, 0.55 - (dy / 800))})`;
  };
  const onE = () => {
    if (!active) return;
    active = false;
    sheetEl.style.transition = '';
    const bg = sheetEl.closest('.sp-modal-bg, .sp-order-bg');
    if (bg) bg.style.background = '';
    if (delta > 80 || vel > 0.5) { sheetEl.style.transform = ''; closeFn(); }
    else { sheetEl.style.transform = 'translateY(0)'; }
  };
  handleEl.addEventListener('touchstart', e => onS(e.touches[0].clientY), { passive: true });
  handleEl.addEventListener('touchmove', e => onM(e.touches[0].clientY), { passive: true });
  handleEl.addEventListener('touchend', onE, { passive: true });
  handleEl.addEventListener('mousedown', e => { e.preventDefault(); onS(e.clientY); });
  window.addEventListener('mousemove', e => { if (active) onM(e.clientY); });
  window.addEventListener('mouseup', () => { if (active) onE(); });
}

// ════════════════════════════════
// CATEGORY MODAL (homepage quick-view)
// ════════════════════════════════
window.openCategoryModal = function(key) {
  if (typeof PRODUCTS === 'undefined') return;
  const cat = PRODUCTS[key];
  if (!cat) return;

  const bg = document.getElementById('spCatModal');
  const tag = bg?.querySelector('.sp-modal-tag');
  const title = bg?.querySelector('.sp-modal-title');
  const grid = bg?.querySelector('.sp-prod-grid');
  const link = bg?.querySelector('.sp-modal-link');
  if (!bg || !grid) return;

  if (tag) tag.textContent = cat.name;
  if (title) title.textContent = `${cat.emoji}  ${cat.name}`;
  if (link) link.href = `/pages/${key}`;

  if (grid.dataset.catKey !== key) {
    grid.dataset.catKey = key;
    grid.innerHTML = '';
    cat.items.forEach((item, idx) => {
      const minP = item.sizes?.[0]?.price ?? 0;
      const price = minP === 0 ? 'Seasonal' : `from \u20B9${minP}`;
      const div = document.createElement('div');
      div.className = 'sp-prod-item';
      div.style.animationDelay = `${Math.min(idx * 25, 250)}ms`;
      div.innerHTML = `
        <div class="sp-prod-item-img">
          <img src="${item.image}" alt="${item.name}" loading="lazy"
               onerror="this.src='https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&q=70'">
        </div>
        <div class="sp-prod-item-info">
          <div class="sp-prod-item-name">${item.name}</div>
          <div class="sp-prod-item-desc">${item.description}</div>
          <div class="sp-prod-item-price">${price}</div>
        </div>`;
      div.addEventListener('click', () => {
        setTimeout(() => window.openOrderPopup(item, cat.name), 50);
      });
      grid.appendChild(div);
    });
  }

  const body = bg.querySelector('.sp-modal-body');
  if (body) body.scrollTop = 0;
  bg.classList.add('open');
  spLock();
  history.pushState({ modal: 'cat' }, '');
};

window.closeCategoryModal = function() {
  const bg = document.getElementById('spCatModal');
  if (!bg) return;
  bg.classList.remove('open');
  const orderOpen = document.getElementById('spOrderPopup')?.classList.contains('open');
  if (!orderOpen) spUnlock();
};

function initCategoryModal() {
  const bg = document.getElementById('spCatModal');
  const close = bg?.querySelector('.sp-modal-close');
  if (!bg) return;
  close?.addEventListener('click', window.closeCategoryModal);
  bg.addEventListener('click', e => { if (e.target === bg) window.closeCategoryModal(); });
  addDragClose(bg.querySelector('.sp-drag-pill'), bg.querySelector('.sp-modal-sheet'), window.closeCategoryModal);
}

// ════════════════════════════════
// ORDER POPUP — Instant 150ms sheet, secure flow
// ════════════════════════════════
window.openOrderPopup = function(item, catName) {
  if (!item) return;
  SP.currentProduct = item;
  SP.currentCatName = catName || '';
  SP.selectedSize = item.sizes?.[0] ?? { size: 'Standard', price: 0 };

  const bg = document.getElementById('spOrderPopup');
  if (!bg) return;

  const thumb = bg.querySelector('.sp-order-thumb');
  const cat = bg.querySelector('.sp-order-head-cat');
  const name = bg.querySelector('.sp-order-head-name');
  const desc = bg.querySelector('.sp-order-desc');
  const sizes = bg.querySelector('.sp-size-options');
  const confirm = bg.querySelector('.sp-confirm-overlay');

  if (thumb) {
    thumb.src = item.image;
    thumb.alt = item.name;
    thumb.onerror = () => { thumb.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200&q=70'; };
  }
  if (cat) cat.textContent = catName || '';
  if (name) name.textContent = item.name;
  if (desc) desc.textContent = item.description;
  if (confirm) confirm.classList.remove('show');

  if (sizes) {
    sizes.innerHTML = '';
    (item.sizes || []).forEach((s, i) => {
      const btn = document.createElement('button');
      btn.className = 'sp-size-btn' + (i === 0 ? ' active' : '');
      btn.textContent = s.price === 0 ? `${s.size} \u2014 Seasonal` : `${s.size} \u2014 \u20B9${s.price}`;
      btn.addEventListener('click', () => {
        SP.selectedSize = s;
        sizes.querySelectorAll('.sp-size-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        updatePrice();
      });
      sizes.appendChild(btn);
    });
  }
  updatePrice();

  bg.classList.add('open');
  if (!SP.locked) spLock();
  history.pushState({ modal: 'order' }, '');
};

function updatePrice() {
  const el = document.querySelector('#spOrderPopup .sp-price-val');
  if (!el || !SP.selectedSize) return;
  el.textContent = SP.selectedSize.price === 0 ? 'Seasonal' : `\u20B9${SP.selectedSize.price}`;
}

window.closeOrderPopup = function() {
  const bg = document.getElementById('spOrderPopup');
  if (!bg) return;
  bg.classList.remove('open');
  const confirm = bg.querySelector('.sp-confirm-overlay');
  if (confirm) confirm.classList.remove('show');
  const catOpen = document.getElementById('spCatModal')?.classList.contains('open');
  if (!catOpen) spUnlock();
};

function showOrderConfirmation() {
  const bg = document.getElementById('spOrderPopup');
  const confirm = bg?.querySelector('.sp-confirm-overlay');
  if (!confirm || !SP.currentProduct || !SP.selectedSize) return;

  const p = SP.currentProduct;
  const s = SP.selectedSize;

  const detailEl = confirm.querySelector('.sp-confirm-detail');
  if (detailEl) {
    detailEl.innerHTML = `
      <div><strong>Item:</strong> ${p.name}</div>
      <div><strong>Category:</strong> ${SP.currentCatName}</div>
      <div><strong>Size:</strong> ${s.size}</div>
      <div><strong>Price:</strong> ${s.price === 0 ? 'Seasonal pricing' : '\u20B9' + s.price}</div>
    `;
  }

  confirm.classList.add('show');
}

function sendWhatsAppOrder() {
  if (!SP.currentProduct || !SP.selectedSize) return;
  const p = SP.currentProduct;
  const s = SP.selectedSize;
  const msg = encodeURIComponent(
    `Hello Sprinkles by Ruchi! \uD83C\uDF82\n\n` +
    `I'd like to order:\n` +
    `\u2022 *${p.name}*\n` +
    `\u2022 Category: ${SP.currentCatName}\n` +
    `\u2022 Size: ${s.size}\n` +
    `\u2022 Price: ${s.price === 0 ? 'Seasonal pricing' : '\u20B9' + s.price}\n\n` +
    `Please confirm availability and delivery details. Thank you! \uD83D\uDE0A`
  );
  window.open(`https://wa.me/${SP.WA}?text=${msg}`, '_blank');
}

function initOrderPopup() {
  const bg = document.getElementById('spOrderPopup');
  const close = bg?.querySelector('.sp-order-close');
  const waBtn = bg?.querySelector('.sp-btn-order-wa');
  const confirmWa = bg?.querySelector('.sp-confirm-wa');
  const confirmBack = bg?.querySelector('.sp-confirm-back-btn');
  if (!bg) return;

  close?.addEventListener('click', window.closeOrderPopup);
  bg.addEventListener('click', e => { if (e.target === bg) window.closeOrderPopup(); });
  addDragClose(bg.querySelector('.sp-drag-pill'), bg.querySelector('.sp-order-sheet'), window.closeOrderPopup);

  waBtn?.addEventListener('click', e => {
    e.preventDefault();
    showOrderConfirmation();
  });

  confirmWa?.addEventListener('click', e => {
    e.preventDefault();
    sendWhatsAppOrder();
  });

  confirmBack?.addEventListener('click', () => {
    const confirm = bg.querySelector('.sp-confirm-overlay');
    if (confirm) confirm.classList.remove('show');
  });
}

// ════════════════════════════════
// CATEGORY PAGE — Populate from blocks or data
// ════════════════════════════════
function initCategoryPage() {
  const grid = document.getElementById('spCatPageGrid');
  if (!grid) return;
  if (typeof PRODUCTS === 'undefined') return;

  const pageEl = document.querySelector('[data-cat-key]');
  const key = pageEl?.dataset.catKey || window.location.pathname.split('/').pop();

  const cat = PRODUCTS[key];
  if (!cat) return;

  // If blocks are already rendering products, skip JS population
  if (grid.children.length > 0) return;

  const emoji = document.getElementById('spCatPageEmoji');
  const title = document.getElementById('spCatPageTitle');
  const desc = document.getElementById('spCatPageDesc');
  const count = document.getElementById('spCatPageCount');

  if (emoji) emoji.textContent = cat.emoji;
  if (title) title.textContent = cat.name;
  if (desc) desc.textContent = cat.description;
  if (count) count.textContent = `${cat.items.length} items`;

  cat.items.forEach((item, idx) => {
    const minP = item.sizes?.[0]?.price ?? 0;
    const price = minP === 0 ? 'Seasonal' : `from \u20B9${minP}`;
    const div = document.createElement('div');
    div.className = 'sp-prod-item';
    div.style.animationDelay = `${Math.min(idx * 30, 300)}ms`;
    div.innerHTML = `
      <div class="sp-prod-item-img">
        <img src="${item.image}" alt="${item.name}" loading="lazy"
             onerror="this.src='https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&q=70'">
      </div>
      <div class="sp-prod-item-info">
        <div class="sp-prod-item-name">${item.name}</div>
        <div class="sp-prod-item-desc">${item.description}</div>
        <div class="sp-prod-item-price">${price}</div>
      </div>`;
    div.addEventListener('click', () => {
      window.openOrderPopup(item, cat.name);
    });
    grid.appendChild(div);
  });
}

// ════════════════════════════════
// BACK BUTTON / ESC
// ════════════════════════════════
window.addEventListener('popstate', () => {
  const orderOpen = document.getElementById('spOrderPopup')?.classList.contains('open');
  const catOpen = document.getElementById('spCatModal')?.classList.contains('open');
  if (orderOpen) window.closeOrderPopup();
  else if (catOpen) window.closeCategoryModal();
});

document.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    const orderOpen = document.getElementById('spOrderPopup')?.classList.contains('open');
    const catOpen = document.getElementById('spCatModal')?.classList.contains('open');
    if (orderOpen) window.closeOrderPopup();
    else if (catOpen) window.closeCategoryModal();
  }
});

// ════════════════════════════════
// SMOOTH SCROLL — Anchor links
// ════════════════════════════════
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const id = a.getAttribute('href').slice(1);
      const el = id ? document.getElementById(id) : null;
      if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    });
  });
}

// ════════════════════════════════
// BACK TO TOP BUTTON
// ════════════════════════════════
function initBackToTop() {
  const btn = document.getElementById('spBackTop');
  if (!btn) return;
  window.addEventListener('scroll', () => {
    btn.classList.toggle('show', window.scrollY > 400);
  }, { passive: true });
  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

// ════════════════════════════════
// CATEGORY PAGE FADE — IntersectionObserver for product cards
// ════════════════════════════════
function initCategoryFade() {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('.sp-cat-page .sp-fade').forEach(el => el.classList.add('visible'));
    return;
  }
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); } });
  }, { threshold: 0.05 });
  document.querySelectorAll('.sp-cat-page .sp-fade:not(.sp-hidden-product)').forEach(el => io.observe(el));
}

// ════════════════════════════════
// PRODUCT SIZE BUTTONS — Category pages
// ════════════════════════════════
function initProductSizeButtons() {
  document.querySelectorAll('.sp-prod-sizes').forEach(sizesEl => {
    const card = sizesEl.closest('.sp-prod-card');
    const priceEl = card?.querySelector('.sp-prod-price');
    sizesEl.querySelectorAll('.sp-prod-size').forEach(btn => {
      btn.addEventListener('click', () => {
        sizesEl.querySelectorAll('.sp-prod-size').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        if (priceEl) {
          const price = btn.dataset.price;
          const label = btn.dataset.label;
          if (price) priceEl.innerHTML = `₹${price} <small>/ ${label}</small>`;
        }
        if (card) {
          card.dataset.price = btn.dataset.price || '';
        }
      });
    });
  });
}

// ════════════════════════════════
// QUANTITY BUTTONS — Category pages
// ════════════════════════════════
// ════════════════════════════════
// QUANTITY BUTTONS — single delegated handler, never double-fires
// ════════════════════════════════
(function () {
  if (window._spQtyBound) return;
  window._spQtyBound = true;
  document.addEventListener('click', function (e) {
    // Check if clicked element is a button inside .sp-prod-qty
    const qtyEl = e.target.closest('.sp-prod-qty');
    if (!qtyEl) return;
    const btn = e.target.closest('button');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const inp = qtyEl.querySelector('input[type="number"]');
    if (!inp) return;
    const v = parseInt(inp.value, 10) || 1;
    const btns = qtyEl.querySelectorAll('button');
    if (btn === btns[0]) {
      // minus
      if (v > 1) inp.value = v - 1;
    } else {
      // plus (last button)
      if (v < 20) inp.value = v + 1;
    }
  }, true); // capture phase — fires before any other handler
}());

function initQtyButtons() { /* delegated — nothing to do */ }

// ════════════════════════════════
// INIT — Wire everything
// ════════════════════════════════
function spInit() {
  initHeader();
  initMobileMenu();
  initHeroSlider();
  initBestSellersCarousel();
  initFadeIn();
  initCategoryFade();
  initLazyImages();
  initCategoryModal();
  initOrderPopup();
  initSmoothScroll();
  initCategoryPage();
  initBackToTop();
  initProductSizeButtons();
  initQtyButtons();
  requestAnimationFrame(() => { requestAnimationFrame(() => { initCarousel(); }); });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', spInit);
} else {
  spInit();
}

window.addEventListener('load', () => {
  if (!SP.carouselTimer) {
    const track = document.querySelector('.sp-carousel-track');
    if (track && track.children.length > 0) initCarousel();
  }
  initLazyImages();
  setTimeout(() => {
    const header = document.querySelector('.sp-hdr');
    if (header && window.scrollY < 50) { header.classList.add('at-hero'); header.classList.remove('scrolled'); }
  }, 200);
});

// ════════════════════════════════
// HERO SLIDER — Auto-advance with dots
// ════════════════════════════════
function initHeroSlider() {
  const slider = document.getElementById('spHeroSlider');
  const dotsW  = document.getElementById('spHeroDots');
  if (!slider) return;
  const slides = slider.querySelectorAll('.sp-hs-slide');
  if (!slides.length) return;

  let current = 0, timer = null;

  function goTo(idx) {
    slides.forEach((s, i) => s.classList.toggle('active', i === idx));
    if (dotsW) {
      dotsW.querySelectorAll('.sp-hs-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
    }
    current = idx;
  }

  function next() { goTo((current + 1) % slides.length); }
  function startAuto() { timer = setInterval(next, 4000); }
  function stopAuto() { clearInterval(timer); }

  // Build dots
  if (dotsW && slides.length > 1) {
    dotsW.innerHTML = '';
    slides.forEach((_, i) => {
      const d = document.createElement('button');
      d.className = 'sp-hs-dot' + (i === 0 ? ' active' : '');
      d.setAttribute('aria-label', `Slide ${i + 1}`);
      d.addEventListener('click', () => { stopAuto(); goTo(i); startAuto(); });
      dotsW.appendChild(d);
    });
  }

  // Touch/swipe support
  let tx = 0;
  slider.addEventListener('touchstart', e => { tx = e.touches[0].clientX; stopAuto(); }, { passive: true });
  slider.addEventListener('touchend', e => {
    const d = tx - e.changedTouches[0].clientX;
    if (Math.abs(d) > 40) d > 0 ? next() : goTo((current - 1 + slides.length) % slides.length);
    startAuto();
  }, { passive: true });

  slider.addEventListener('mouseenter', stopAuto);
  slider.addEventListener('mouseleave', startAuto);

  goTo(0);
  if (slides.length > 1) startAuto();
}

// ════════════════════════════════
// BEST SELLERS CAROUSEL — Arrow buttons + swipe
// ════════════════════════════════
function initBestSellersCarousel() {
  const track  = document.getElementById('spBestTrack');
  const btnL   = document.getElementById('spBestLeft');
  const btnR   = document.getElementById('spBestRight');
  if (!track) return;

  const cards = track.querySelectorAll('.sp-prod-card');
  if (!cards.length) return;

  let bsTimer = null;

  function cardWidth() {
    const c = cards[0];
    if (!c) return 0;
    const gap = parseFloat(getComputedStyle(track).gap) || 14;
    return c.getBoundingClientRect().width + gap;
  }

  function scrollBy(dir) {
    // dir: +1 = right, -1 = left
    const w = cardWidth();
    track.scrollBy({ left: dir * w, behavior: 'smooth' });
  }

  function autoAdvance() {
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft >= maxScroll - 10) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      scrollBy(1);
    }
  }

  function startBsAuto() { bsTimer = setInterval(autoAdvance, 4500); }
  function stopBsAuto()  { clearInterval(bsTimer); }

  btnL?.addEventListener('click', () => { stopBsAuto(); scrollBy(-1); startBsAuto(); });
  btnR?.addEventListener('click', () => { stopBsAuto(); scrollBy(1); startBsAuto(); });

  // Touch swipe — native scrolling handles it; just stop/start auto
  track.addEventListener('touchstart', () => stopBsAuto(), { passive: true });
  track.addEventListener('touchend', () => startBsAuto(), { passive: true });

  track.parentElement?.addEventListener('mouseenter', stopBsAuto);
  track.parentElement?.addEventListener('mouseleave', startBsAuto);

  startBsAuto();
  let rt;
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { track.scrollTo({ left: 0, behavior: 'instant' }); }, 250); });
}
